import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AxiosError } from 'axios';
import { get } from 'lodash';
import { PayloadAction } from 'typesafe-actions';

import { PayloadApiSuccess } from '../actions/sagaApi';

/**
 * Hook for actions that update data. Based on:
 *
 * - https://www.apollographql.com/docs/react/api/react-hooks/#usemutation
 *
 * @param fn the custom action function `resource.customAction`
 *
 * @return https://www.apollographql.com/docs/react/api/react-hooks/#result-2
 *
 * @example
 *
 * import { useMutation } from 'lib/hooks';
 * import { usersResource } from 'lib/resources';
 *
 * const [ updatePassword, { loading }] = useMutation(usersResource.updatePassword);
 *
 * ...
 * updatePassword(newPassword);
 */
function useMutation<A extends any[], T extends string, R>(
  fn?: (...args: A) => PayloadAction<T, R>,
): [(...args: A) => any, { loading: boolean; called: boolean; response?: PayloadApiSuccess<any>; error?: AxiosError }] {
  const dispatch = useDispatch();
  const [called, setCalled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PayloadApiSuccess<any>>();
  const [error, setError] = useState<AxiosError>();

  const mutate = useCallback(
    (...args: A) => {
      if (!fn) {
        return;
      }
      setCalled(true);
      setLoading(true);
      setResponse(undefined);
      setError(undefined);
      const actionPayload = fn(...args);
      const meta = get(actionPayload, 'payload.meta', {});
      dispatch({
        ...actionPayload,
        payload: {
          ...actionPayload.payload,
          meta: {
            ...meta,
            callbackSuccess: (res: PayloadApiSuccess<any>) => {
              setLoading(false);
              setResponse(res);
              if (meta.callbackSuccess) {
                meta.callbackSuccess(res);
              }
            },
            callbackError: (res?: AxiosError) => {
              setLoading(false);
              setError(res);
              if (meta.callbackError) {
                meta.callbackError(res);
              }
            },
          },
        },
      });
    },
    // eslint-disable-next-line
    [dispatch, fn, setCalled, setLoading, setResponse, setError],
  );

  return [mutate, { loading, called, response, error }];
}

export default useMutation;
